import { useState, useContext, useMemo } from "react";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { OverallDowntimeSummaryPieChart } from "components/OverallDowntimeSummaryPieChart/OverallDowntimeSummaryPieChart.view";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useStyles } from "./OverallSummarySlide.styles";
import { OverallDowntimeSummaryPieChartLegends } from "components/OverallDowntimeSummaryPieChartLegends/OverallDowntimeSummaryPieChartLegends.view";
import { HMIUptimeDowntimeBar } from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBar.view";
import { OverallDowntimeSummaryPieChartCenteredMetric } from "components/OverallDowntimeSummaryPieChartCenteredMetric/OverallDowntimeSummaryPieChartCenteredMetric.view";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { NoStatsDataSlide } from "../index";
import { GraphsNotEnoughDataInMinutes } from "components/GraphsNotEnoughDataInMinutes/GraphsNotEnoughDataInMinutes.view";
import { IGraphProps } from "./interfaces";
import { buildPieChartData, hasOnlyNaNValues } from "./overallSummaryTransforms";

export const OverallSummarySlide = ({
    downtimeEvents,
    countryCode,
}: Pick<IGraphProps, "downtimeEvents" | "countryCode">) => {
    const { displayUnit } = useContext(HMIContext);
    const [highlight, setHighlight] = useState({ name: "", value: 0 });
    const { t, i18n } = useTranslation();
    const { store } = useContext(HMICachedDataContext);
    const classes = useStyles();

    const pieChartData = useMemo(
        () => buildPieChartData(downtimeEvents, displayUnit, i18n.language),
        [displayUnit, downtimeEvents, i18n.language],
    );

    const totalEventsInMinutesOrOccurrences = useMemo(
        () => pieChartData.reduce((acc, current) => acc + current.value, 0),
        [pieChartData],
    );

    const noInitialDataInMinutes = useMemo(() => hasOnlyNaNValues(pieChartData), [pieChartData]);

    return (
        <Box className={classes.slideRoot}>
            <Typography variant="h1" className={classes.title}>
                {t("HMI.overall_summary.title")}
            </Typography>
            {downtimeEvents && downtimeEvents.length > 0 ? (
                <>
                    {store.machineCenter?.production_line.production_line_shifts.length! > 0 && (
                        <HMIUptimeDowntimeBar />
                    )}
                    <Grid container className={classes.graphContainer}>
                        {noInitialDataInMinutes ? (
                            <Grid item xs={12} className={classes.graphGridItemContainer}>
                                <GraphsNotEnoughDataInMinutes />
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={8} className={classes.graphGridItemContainer}>
                                    <OverallDowntimeSummaryPieChart
                                        data={pieChartData}
                                        totalEventsInMinutesOrOccurrences={totalEventsInMinutesOrOccurrences!}
                                        pieChartCenteredMetric={OverallDowntimeSummaryPieChartCenteredMetric(
                                            highlight!,
                                        )}
                                        setHighlight={setHighlight}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <OverallDowntimeSummaryPieChartLegends
                                        pieChartData={pieChartData}
                                        totalEventsInMinutesOrOccurrences={totalEventsInMinutesOrOccurrences!}
                                        countryCode={countryCode}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </>
            ) : (
                <NoStatsDataSlide />
            )}
        </Box>
    );
};
